import { cn } from '@repo/tailwind-config/tailwind.functions'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import { DeleteIcon, PlusIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { createCenterExceptionalExpense } from '../../../../api/centers/center-exceptional-expenses/create-center-exceptional-expense'
import { deleteCenterExceptionalExpense } from '../../../../api/centers/center-exceptional-expenses/delete-center-exceptional-expense'
import { getCenterExceptionalExpenses } from '../../../../api/centers/center-exceptional-expenses/get-center-exceptional-expenses'
import { updateCenterExceptionalExpenses } from '../../../../api/centers/center-exceptional-expenses/update-center-exceptional-expenses'
import { getCenter } from '../../../../api/centers/get-center'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterExceptionalExpense,
  CenterExceptionalExpenseInput,
  UpdateCenterExceptionalExpenseInput
} from '../../../../lib/types/models/center/center-exceptional-expenses'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton,
  UploadInvoiceComponent,
  userInvoiceColorMapping
} from './center.ui'
import {
  CenterCostsTableProps,
  getCenterMiscellaneousCostsColumns,
  mapCenterExceptionalExpensesToInput
} from './utils'

export function CenterMiscellaneousCostsTable(props: DataProps<Center, CenterCostsTableProps>) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [editable, setEditable] = useState(false)
  const [miscellaneousCosts, setMiscellaneousCosts] =
    useState<UpdateCenterExceptionalExpenseInput[]>()

  const {
    id: centerId,
    data: center,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))
  const { centerCostsView, lock, unlock, isPending, uploadInvoice, deleteInvoice } =
    useCenterCosts({
      centerId,
      costType,
      table: 'miscellaneous'
    })

  const { data: centerExceptionalExpenses, refetch } = useQuery({
    queryKey: ['center-exceptional-expenses', centerId, costType],
    queryFn: () =>
      getCenterExceptionalExpenses(centerId, {
        sort: { createdAt: 'asc' },
        ...(costType === 'original-cost' && { filter: { isFinal: false } })
      })
  })

  const createCenterExceptionalExpenseMutation = useMutation({
    mutationFn: (data: CenterExceptionalExpenseInput) =>
      createCenterExceptionalExpense(centerId, data),
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      centerCostsView.refetch()
    }
  })

  const deleteCenterExceptionalExpenseMutation = useMutation({
    mutationFn: (id: number) => deleteCenterExceptionalExpense(centerId, id),
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      centerCostsView.refetch()
    }
  })

  const updateCenterExceptionalExpensesMutation = useMutation({
    mutationFn: (data: UpdateCenterExceptionalExpenseInput[]) =>
      updateCenterExceptionalExpenses(centerId, data),
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      centerCostsView.refetch()
      setMiscellaneousCosts(undefined)
    }
  })

  const hasAnIncompleteExceptionalExpense = useMemo(
    () =>
      centerExceptionalExpenses?.data.some(
        (e) => !e.reason?.trim() || e.amount === undefined || e.amount === null || e.amount === 0
      ),
    [centerExceptionalExpenses?.data]
  )

  const isDisabled =
    createCenterExceptionalExpenseMutation.isPending ||
    deleteCenterExceptionalExpenseMutation.isPending ||
    updateCenterExceptionalExpensesMutation.isPending ||
    isPending

  const columns: EditableTableColumn<CenterExceptionalExpense>[] = [
    ...getCenterMiscellaneousCostsColumns(costType, currency, t),
    ...(costType === 'final-cost'
      ? ([
          {
            accessorKey: 'invoices',
            header: t('center.costs.tefl.teachers.and.leaders.table.header.upload.invoices'),
            centerCell: true,
            cell: ({ row }) => (
              <UploadInvoiceComponent
                variant={userInvoiceColorMapping[user.role]}
                invoice={row.original.invoice}
                readOnly={
                  row.original.validated ||
                  (!editable && mode === 'action-edit') ||
                  mode === 'readonly'
                }
                disabled={isDisabled}
                onDelete={async () => {
                  await deleteInvoice.mutateAsync(row.original.id)
                  refetch()
                }}
                onChange={async (file) => {
                  await uploadInvoice.mutateAsync({ id: row.original.id, file })
                  refetch()
                }}
              />
            )
          }
        ] as EditableTableColumn<CenterExceptionalExpense>[])
      : []),

    {
      header: '',
      accessorKey: 'actions',
      cell: ({ row }) =>
        !row.original.validated &&
        ((!editable && mode === 'action-edit') || mode === 'free-edit') && (
          <ActionButton
            icon={DeleteIcon}
            variant={'danger'}
            disabled={isDisabled}
            onClick={() => deleteCenterExceptionalExpenseMutation.mutateAsync?.(row.original.id)}
          />
        )
    }
  ]

  if (!centerExceptionalExpenses?.data || !center) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        title={t('center.costs.miscellaneous.title')}
        onCancel={() => setMiscellaneousCosts(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: center.isHostFamiliesLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
        onSave={async () => {
          if (miscellaneousCosts) {
            await updateCenterExceptionalExpensesMutation.mutateAsync(miscellaneousCosts)
            await Promise.all([centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterExceptionalExpense>
        isEditable={editable}
        isLoading={
          createCenterExceptionalExpenseMutation.isPending ||
          deleteCenterExceptionalExpenseMutation.isPending ||
          updateCenterExceptionalExpensesMutation.isPending
        }
        data={centerExceptionalExpenses.data}
        columns={columns}
        footerHeaderClassName={'border-t'}
        onDataChange={(_, data) => {
          const mappedData = data.map((item) =>
            mapCenterExceptionalExpensesToInput(item, costType)
          )
          if (mode === 'free-edit') updateCenterExceptionalExpensesMutation.mutate(mappedData)
          else setMiscellaneousCosts(mappedData)
        }}
        footerHeader={
          !editable ? null : (
            <Button
              icon={PlusIcon}
              label={t('center.costs.miscellaneous.table.button.add.miscellaneous')}
              disabled={
                createCenterExceptionalExpenseMutation.isPending ||
                hasAnIncompleteExceptionalExpense
              }
              onClick={() => createCenterExceptionalExpenseMutation.mutate({ reason: '' })}
            />
          )
        }
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t('center.costs.miscellaneous.table.footer.total.miscellaneous')
            }
            total={centerCostsView.data?.totalExceptionalExpenseCost}
            initialAmount={centerCostsView.data?.totalExceptionalExpenseCost}
            finalAmount={centerCostsView.data?.finalTotalExceptionalExpenseCost}
            balance={centerCostsView.data?.exceptionalExpenseBalance}
          />
        }
      />
    </div>
  )
}
